import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  Lead,
  Profile,
  ProfileLinkConfig,
  SkillTag,
} from "../../../../../client";
import {
  useGetLink,
  useGetProfile,
  useUpdateLink,
} from "../../../../../services";
import { LinkCreatorPersonalBlock } from "./LinkCreatorPersonalBlock";
import { LinkCreatorProjects } from "./LinkCreatorProjects";
import { LinkCreatorSideMenu } from "./LinkCreatorSideMenu";
import { LinkCreatorWork } from "./LinkCreatorWork";
import { LinkCreatorEducationBlock } from "./LinkCreatorEducationBlock";
import { LinkCreatorSkills } from "./LinkCreatorSkills";
import { LinkCreatorProfileSummary } from "./LinkCreatorProfileSummary";
import { useStatsigClient } from "../../../../../hooks";
import { useGetLinkOptions } from "../../../../../services/link/get-link-settings";
import { LinkCreatorHighlightedProjects } from "./LinkCreatorHighlightedProjects";
import { useGetBackgroundJob } from "../../../../../services/jobs";
import { MagicLoadingCard } from "../../../member/components/MagicLoadingCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LeadRowCard } from "../card/LeadRowCard";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { hasAssignmentData, getLeadSkillTags } from "../../../../../utils/get-lead-skills";

interface Props {
  lead: Lead;
  organizationId: string;
  onShowAddSkillsDialog: (
    skillTags: SkillTag[],
    addingProfileSummarySkills: boolean,
  ) => void;
  skillsAddedFromDialog: SkillTag[];
  profileSummarySkillsAddedFromDialog: SkillTag[];
  onAddedCustomSkills: () => void;
  isDefaultLead: boolean;
}

type OrganizedSkills = {
  mainCategory: string;
  skills: string[];
}[];

export const LinkCreatorDrawerContent = memo(
  ({
    lead,
    organizationId,
    onShowAddSkillsDialog,
    skillsAddedFromDialog,
    profileSummarySkillsAddedFromDialog,
    onAddedCustomSkills,
    isDefaultLead,
  }: Props) => {
    const statsigClient = useStatsigClient();
    const [checkForBackgroundJob, setCheckForBackgroundJob] = useState(true);
    const [isTranslating, setIsTranslating] = useState(false);
    const { data: profile } = useGetProfile(
      {
        organizationId,
        profileId: lead.profileId ?? "",
      },
      {
        enabled: !!lead.profileId,
      },
    );

    const {
      data: link,
      refetch,
      isLoading: isLinkLoading,
    } = useGetLink(lead.profileLinkId ?? "", {
      enabled: !!lead.profileLinkId,
    });

    const { data: linkSettings } = useGetLinkOptions(lead.profileLinkId ?? "", {
      enabled: !!lead.profileLinkId,
    });

    const {
      data: projectWriterBgJobData,
      isSuccess,
      isError,
      error,
      fetchStatus,
    } = useGetBackgroundJob(lead.profileId ?? "", "project-writer", {
      refetchInterval: checkForBackgroundJob ? 2000 : false,
    });

    // Store the previous state of projectWriterBgJobData?.isRunning
    const prevProjectWriterBgJobIsRunning = useRef(
      projectWriterBgJobData?.isRunning,
    );

    // Poll when generating profile summary
    useEffect(() => {
      if (!link?.isGeneratingProfileSummary) return;

      const interval = setInterval(() => {
        refetch();
      }, 2000);

      return () => clearInterval(interval);
    }, [link?.isGeneratingProfileSummary, refetch]);

    useEffect(() => {
      // Check if projectWriterBgJobData exists and the job has just finished (isRunning changed from true to false)
      if (
        prevProjectWriterBgJobIsRunning.current === true &&
        projectWriterBgJobData &&
        projectWriterBgJobData.isRunning === false
      ) {
        setCheckForBackgroundJob(false);
        refetch(); // Call refetch when the background job finishes
      }
      // Update the ref with the current value for the next render
      prevProjectWriterBgJobIsRunning.current =
        projectWriterBgJobData?.isRunning;
    }, [projectWriterBgJobData, refetch]);

    // Fix profileData extraction with proper null checking
    const profileData = link?.accountProfile.profile_data as
      | Profile
      | undefined;

    const { mutate: updateLink, variables, reset } = useUpdateLink();

    // Reset variables when component mounts or when link changes
    useEffect(() => {
      reset();
    }, [reset, link?.accountProfile.profileLinkId]);

    const mergedLinkConfig = useMemo(() => {
      if (!link?.accountProfile.link_config_data) return undefined;

      // Only merge if there's an active mutation
      if (
        variables?.body &&
        variables?.path.profileLink === link.accountProfile?.profileLinkId
      ) {
        return {
          ...link.accountProfile.link_config_data,
          ...variables.body,
        };
      }

      return link.accountProfile.link_config_data;
    }, [
      link?.accountProfile?.link_config_data,
      variables,
      link?.accountProfile?.profileLinkId,
    ]);

    // Use mergedLinkConfig instead of creating link_config_data directly
    const link_config_data = mergedLinkConfig;

    // Use mergedLinkConfig instead of creating link_config_data directly

    const handleUpdateLink = (update: Partial<ProfileLinkConfig>) => {
      if (!lead.profileLinkId) return;

      if (!link?.accountProfile?.link_config_data) return;

      const mergedBody = {
        ...link.accountProfile.link_config_data,
        ...update,
      };

      updateLink({
        path: { profileLink: lead.profileLinkId },
        body: mergedBody,
      });
    };

    const disabled = false;

    if (!link_config_data) {
      return <div></div>;
    }

    return (
      <div className="flex flex-col xl:flex-row gap-8 xl:gap-32 p-4 sm:px-10 sm:py-12 overflow-y-auto max-w-full">
        <div
          className={`flex flex-col flex-1 gap-10 h-max min-w-0 transition-opacity duration-200 ${isTranslating ? "opacity-50" : "opacity-100"}`}
        >
          {isDefaultLead && (
            <LeadRowCard className="text-xs w-full" type="dashed">
              <div className="flex flex-1 items-center justify-start gap-3">
                <FontAwesomeIcon
                  width={14}
                  height={14}
                  icon={faInfoCircle}
                  className="w-4 h-4 shrink-0"
                />

                <p>
                  These settings will be used for every new Profile Lead
                  created. Changes will not affect existing leads.
                </p>
              </div>
            </LeadRowCard>
          )}
          <LinkCreatorPersonalBlock
            linkConfigData={link_config_data}
            disabled={disabled}
            handleUpdateLink={handleUpdateLink}
            profile={profile}
            profilePitch={profileData?.pitch ?? ""}
            title={
              typeof link.accountProfile?.profile_data?.title === "string"
                ? (link.accountProfile?.profile_data?.title ?? "")
                : ""
            }
            organizationId={organizationId}
            leadId={lead.id!}
            linkId={lead?.profileLinkId!}
            assignmentInfoAdded={hasAssignmentData(lead)}
            isDefaultLead={isDefaultLead}
          />
          {link.isGeneratingProfileSummary ? (
            <MagicLoadingCard>
              <span>Generating profile summary</span>
            </MagicLoadingCard>
          ) : (
            <LinkCreatorProfileSummary
              linkConfigData={link_config_data}
              assignmentPitch={link_config_data.assignmentPitch || ""}
              setAssignmentPitch={(value) =>
                handleUpdateLink({ assignmentPitch: value })
              }
              isGeneratingAssigmentPitch={false}
              onShowAddSkillsDialog={(selectedSkills) => {
                onShowAddSkillsDialog(selectedSkills, true);
              }}
              handleUpdateLink={handleUpdateLink}
              assignmentInfoAdded={hasAssignmentData(lead)}
              organizationId={organizationId}
              leadId={lead.id!}
              linkId={lead.profileLinkId!}
              skillsAddedFromDialog={profileSummarySkillsAddedFromDialog}
              onAddedCustomSkills={onAddedCustomSkills}
              backendIsGeneratingProfileSummary={
                link.isGeneratingProfileSummary!
              }
              isDefaultLead={isDefaultLead}
            />
          )}
          {projectWriterBgJobData?.isRunning && (
            <MagicLoadingCard>
              <span>Generating highlighted projects</span>
            </MagicLoadingCard>
          )}
          {link_config_data.highlightedProjects && (
            <LinkCreatorHighlightedProjects
              profileData={profileData}
              linkConfigData={link_config_data}
              handleUpdateLink={handleUpdateLink}
              isDefaultLead={isDefaultLead}
            />
          )}
          {link_config_data &&
          link_config_data.skillsSummaryConfig?.placeInTop === true ? (
            <LinkCreatorSkills
              disabled={isDefaultLead}
              linkConfigData={link_config_data}
              handleUpdateLink={handleUpdateLink}
              profileData={profileData}
              nonMatchingSkillTags={lead.nonMatchingSkillTags ?? []}
              onAddSkill={(newSkillName: string) => {}}
              leadSkillTags={getLeadSkillTags(lead)}
              onShowAddSkillsDialog={(selectedSkills) =>
                onShowAddSkillsDialog(selectedSkills, false)
              }
              skillsAddedFromDialog={skillsAddedFromDialog}
              onAddedCustomSkills={onAddedCustomSkills}
              isDefaultLead={isDefaultLead}
            />
          ) : null}
          <LinkCreatorWork
            profileData={profileData}
            linkConfigData={link_config_data}
            handleUpdateLink={handleUpdateLink}
            isDefaultLead={isDefaultLead}
          />
          <LinkCreatorProjects
            profileData={profileData}
            linkConfigData={link_config_data}
            handleUpdateLink={handleUpdateLink}
            isDefaultLead={isDefaultLead}
          />
          <LinkCreatorEducationBlock
            disabled={disabled}
            linkConfigData={link_config_data}
            handleUpdateLink={handleUpdateLink}
            organizationId={organizationId}
            leadId={lead.id!}
            linkId={lead.profileLinkId!}
            profileData={profileData}
          />
          {link_config_data &&
          !link_config_data.skillsSummaryConfig?.placeInTop ? (
            <LinkCreatorSkills
              disabled={isDefaultLead}
              linkConfigData={link_config_data}
              handleUpdateLink={handleUpdateLink}
              profileData={profileData}
              nonMatchingSkillTags={lead.nonMatchingSkillTags ?? []}
              onAddSkill={(newSkillName: string) => {}}
              leadSkillTags={getLeadSkillTags(lead)}
              onShowAddSkillsDialog={(selectedSkills) =>
                onShowAddSkillsDialog(selectedSkills, false)
              }
              skillsAddedFromDialog={skillsAddedFromDialog}
              onAddedCustomSkills={onAddedCustomSkills}
              isDefaultLead={isDefaultLead}
            />
          ) : null}
        </div>

        <LinkCreatorSideMenu
          disabled={disabled}
          linkConfig={link_config_data}
          onUpdate={handleUpdateLink}
          organizationId={organizationId}
          leadId={lead.id!}
          linkId={lead.profileLinkId!}
          profileId={lead.profileId ?? ""}
          linkOptions={linkSettings}
          onTranslationStarted={() => setIsTranslating(true)}
          onTranslationEnded={() => {
            setTimeout(() => {
              setIsTranslating(false);
            }, 3000);
          }}
          isLinkLoading={isLinkLoading}
          isDefaultLead={isDefaultLead}
        />
      </div>
    );
  },
);
